import type { PhoneNumber } from 'lib-shared-legacy-types';
import map from 'lodash/map';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';

import type { RootReducerStateT } from 'app/reducers/rootReducer';
import { selectFlowLanguagesSorted } from 'app/utils/reselect';
import { actions as telephonyActions } from 'components/settings/actions/telephony';
import { audioGetAll } from 'components/shared/audio/reducers/audio';
import { hasFeature } from 'components/shared/featureFlag';
import type { GetNumbersByQueryPayloadT } from 'components/telephony/api/types';
import type { NodeDataT } from 'fable/flowEditor';
import { withRouter } from 'hocs/router';
import { bindActionToPromise } from 'utils/sagaApiGeneratorHelpers';

import {
  changeActive,
  changeName,
  changeNumber,
  fetchFlow,
  flushFlow,
  removeLocalFlow,
  unSavedChanged,
  updateFlow,
} from '../actions/callFlowActions';
import { flowLanguagesActions } from '../actions/flowLanguages';
import type { FlowCallActionT, FlowCallFlowT } from '../api/types';
import PowerCallFlow, { FlowCallOwnPropsT } from '../components/PowerCallFlow';

export type FlowDataT = NodeDataT<FlowCallActionT[]>;

const { telephonyGetAllNumbers, telephonyGetNumbersByQuery } = telephonyActions;
const { flowGetLanguages } = flowLanguagesActions;

const getId = (_state: RootReducerStateT, ownProps: FlowCallOwnPropsT) =>
  ownProps.params.flowId;
const getNodes = (state: RootReducerStateT): Record<string, FlowCallActionT> =>
  state.nodes;
const getNumber = (state: RootReducerStateT, ownProps: FlowCallOwnPropsT) =>
  state.callFlow.number || ownProps.location.state?.flowNumber || '';
const getName = (state: RootReducerStateT) => state.callFlow.name;
const getActive = (state: RootReducerStateT) => state.callFlow.active;
const getAllAnnouncements = (state: RootReducerStateT) => state.audio.allAudios;

const selectFlowData = createSelector(
  [getId, getNodes, getNumber, getName, getActive],
  (
    id: string | undefined,
    nodes: Record<string, FlowCallActionT>,
    number: string,
    name: string,
    active: boolean,
  ) => {
    return {
      actions: map(nodes, (n) => n),
      active,
      created_at: '',
      id: id ?? '',
      name,
      number,
      updated_at: '',
    };
  },
);

const mapStateToProps = (
  state: RootReducerStateT,
  ownProps: FlowCallOwnPropsT,
) => {
  return {
    announcements: getAllAnnouncements(state) as any,
    flowData: selectFlowData(state, ownProps),
    flowLanguages: selectFlowLanguagesSorted(state),
    hasUnSavedChanges: state.callFlow.hasUnSavedChanges,
    id: ownProps.params.flowId || null,
    isActive: state.callFlow.active,
    isCanvasEnabled: hasFeature(state, 'routing-use-canvas'),
    isIntegrationsEnabled: hasFeature(state, 'integrations-for-phone'),
    name: state.callFlow.name,
    number: state.callFlow.number,
    status: state.callFlow.status,
    telephonyState: state.settings.telephony,
    agentDisplayName: state.settings.profile.profile.name,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    changeActive: (isActive: boolean) => dispatch(changeActive(isActive)),
    changeName: (name: string) => dispatch(changeName(name)),
    changeNumber: (phoneNumber: PhoneNumber) =>
      dispatch(changeNumber(phoneNumber.e164_format)),
    fetchFlow: (id: null | string) => fetchFlow(id)(dispatch),
    flowGetLanguages: bindActionToPromise(dispatch, flowGetLanguages),
    flushFlow: () => dispatch(flushFlow()),
    getAllAudios: () => dispatch(audioGetAll()),
    getAllNumbers: () => dispatch(telephonyGetAllNumbers()),
    getNumbersByQuery: (payload: GetNumbersByQueryPayloadT) =>
      dispatch(telephonyGetNumbersByQuery(payload)),
    removeLocalFlow: () => dispatch(removeLocalFlow()),
    setUnSavedChanges: (hasChanged: boolean) =>
      unSavedChanged(hasChanged)(dispatch),
    updateFlow: (flow: FlowCallFlowT, initialActions: FlowCallActionT[]) =>
      dispatch(updateFlow(flow, initialActions)),
  };
};

export type FlowCallStatePropsT = ReturnType<typeof mapStateToProps>;
export type FlowCallDispatchPropsT = ReturnType<typeof mapDispatchToProps>;

export default withRouter(
  connect<
    FlowCallStatePropsT,
    FlowCallDispatchPropsT,
    FlowCallOwnPropsT,
    RootReducerStateT
  >(
    mapStateToProps,
    mapDispatchToProps,
  )(PowerCallFlow),
);
